import React, { useEffect, useState } from 'react'
import { IonButton, IonCol, IonContent, IonGrid, IonHeader, IonPage, IonRow } from '@ionic/react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { useOktaAuth } from '@okta/okta-react'
import LoginFormComponent from '../loginForm/LoginForm.component'
import './Login.page.css'
import { getUserSelector } from '../../user/selectors'
import { AppRouterConstants } from '../../../components/appRouter/AppRouter.constants'
import { AuthenticationService } from '../services/authentication.service'
import { isOffline } from '../../../utilities'
import { useTranslation } from 'react-i18next'
import { IBeforeInstallPromptEvent, HandleBeforeInstallPromptListener } from '../store/login.types'

const Login: React.FC = () => {
  let { authState } = useOktaAuth()
  const history = useHistory()
  const user = useSelector(getUserSelector)
  const { t } = useTranslation()

  const [deferredPrompt, setDeferredPrompt] = useState<IBeforeInstallPromptEvent | null>(null)
  const [showPWAInstallPrompt, setShowPWAInstallPrompt] = useState(false)

  useEffect(() => {
    if (authState?.isAuthenticated) {
      history.push(AppRouterConstants.CREW_ROUTE)
    }
  }, [authState])

  useEffect(() => {
    if ('BeforeInstallPromptEvent' in window) {
      const handleBeforeInstallPrompt: HandleBeforeInstallPromptListener = (event: IBeforeInstallPromptEvent) => {
        event.preventDefault()
        setDeferredPrompt(event)
        setShowPWAInstallPrompt(true)
      }
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      }
    }
  }, [])

  const handleInstallClick = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt()
      deferredPrompt.userChoice.then(() => {
        setDeferredPrompt(null)
        setShowPWAInstallPrompt(false)
      })
    }
  }

  const handleCancelInstallClick = () => {
    if (deferredPrompt) {
      setDeferredPrompt(null)
      setShowPWAInstallPrompt(false)
    }
  }

  if (isOffline()) {
    const offlineAuthState = AuthenticationService.getOfflineAuthState(user.userName)

    if (offlineAuthState) {
      authState = offlineAuthState
    }
  } else {
    if (user.authState) {
      authState = user.authState
    }
  }

  const getPwaInstallPrompt = () => {
    return (
      <IonHeader data-testid='PwaInstallPrompt'>
        <IonRow>
          {t('login.loginForm.pwaInstallPromptMessage')}
          <IonButton onClick={handleInstallClick} data-testid='Install'>
            {t('login.loginForm.installButton')}
          </IonButton>
          <IonButton onClick={handleCancelInstallClick} data-testid='CancelInstall'>
            {t('common.buttons.Cancel')}
          </IonButton>
        </IonRow>
      </IonHeader>
    )
  }

  return (
    <IonPage>
      <IonContent fullscreen>
        {showPWAInstallPrompt && getPwaInstallPrompt()}
        <div className='login-screen'>
          <IonGrid>
            <IonRow>
              <IonCol sizeSm='8' offsetSm='2' sizeMd='6' offsetMd='3' sizeLg='6' offsetLg='3' sizeXl='4' offsetXl='4'>
                <LoginFormComponent />
              </IonCol>
            </IonRow>
          </IonGrid>
        </div>
      </IonContent>
    </IonPage>
  )
}

export default Login
